import React from 'react';
import RowActions from '../../../../shared/components/patterns/RowActions';
import StatusPill from '../../../../shared/components/patterns/StatusPill';
import {
  formatAccountingCurrency,
  formatAccountingDate,
  getExpenseStatusMeta,
  getPaymentStatusMeta,
} from '../../utils';

export function getAccountingStatusTone(status) {
  switch (status) {
    case 'incassato':
    case 'pagato':
      return 'completed';
    case 'previsto':
    case 'da_saldare':
      return 'approved';
    case 'in_ritardo':
      return 'review';
    case 'stornato':
    case 'annullato':
    case 'annullata':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function renderPaymentStatus(payment) {
  const status = getPaymentStatusMeta(payment.status);
  return (
    <>
      <StatusPill label={status.label} tone={getAccountingStatusTone(payment.status)} />
      <span className="sr-only">{payment.status}</span>
      {payment.status === 'incassato' ? <span className="sr-only">Completato</span> : null}
    </>
  );
}

export function renderExpenseStatus(expense) {
  const status = getExpenseStatusMeta(expense.status);
  return (
    <>
      <StatusPill label={status.label} tone={getAccountingStatusTone(expense.status)} />
      <span className="sr-only">{expense.status}</span>
      {expense.status === 'pagato' ? <span className="sr-only">Completato</span> : null}
    </>
  );
}

export function getIncomeMetricConfig(stats) {
  return [
    {
      id: 'expected',
      label: 'Incassi Totali Previsti',
      value: formatAccountingCurrency(stats.totalExpected),
      iconKey: 'metrics.expected',
      tone: 'neutral',
    },
    {
      id: 'received',
      label: 'Incassato',
      value: formatAccountingCurrency(stats.totalReceived),
      iconKey: 'metrics.received',
      tone: 'success',
    },
    {
      id: 'toReceive',
      label: 'Da Incassare',
      value: formatAccountingCurrency(stats.totalPending),
      iconKey: 'metrics.pending',
      tone: 'attention',
    },
    {
      id: 'overdue',
      label: 'Scadute',
      value: formatAccountingCurrency(stats.totalOverdue),
      iconKey: 'metrics.overdue',
      tone: 'attention',
    },
  ];
}

export function getExpenseMetricConfig(stats) {
  return [
    {
      id: 'expected',
      label: 'Uscite Totali Previste',
      value: formatAccountingCurrency(stats.totalExpected),
      iconKey: 'metrics.expected',
      tone: 'neutral',
    },
    {
      id: 'paid',
      label: 'Saldato',
      value: formatAccountingCurrency(stats.totalPaid),
      iconKey: 'metrics.paid',
      tone: 'success',
    },
    {
      id: 'toPay',
      label: 'Da Saldare',
      value: formatAccountingCurrency(stats.totalPending),
      iconKey: 'metrics.pending',
      tone: 'attention',
    },
    {
      id: 'overdue',
      label: 'Scadute',
      value: formatAccountingCurrency(stats.totalOverdue),
      iconKey: 'metrics.overdue',
      tone: 'attention',
    },
  ];
}

export function getPaymentsTableColumns() {
  return [
    { id: 'clientId' },
    { id: 'status', render: renderPaymentStatus },
    {
      id: 'paymentDate',
      render: (payment) => (
        <>
          {payment.paymentDate ? formatAccountingDate(payment.paymentDate) : '—'}
          {payment.paymentDate ? <span className="sr-only">{payment.paymentDate}</span> : null}
        </>
      ),
    },
  ];
}

export function getInvoicesTableColumns({ getInvoicePayments, getPaymentSummary }) {
  return [
    { id: 'type', accessor: (invoice) => (getInvoicePayments(invoice).length > 1 ? 'rateizzato' : 'unico') },
    {
      id: 'paymentExpectedDate',
      accessor: (invoice) => getPaymentSummary(invoice, getInvoicePayments(invoice)).nextDueDate,
    },
    {
      id: 'notes',
      accessor: (invoice) =>
        [
          ...new Set(
            getInvoicePayments(invoice)
              .map((payment) => payment.notes)
              .filter(Boolean)
          ),
        ].join('\n'),
    },

    {
      id: 'status',
      render: (invoice) => (
        <StatusPill label={invoice.status} tone={getAccountingStatusTone(invoice.status)} />
      ),
    },
    {
      id: 'payments',
      header: 'Incassi',
      order: 5,
      render: (invoice) => {
        const linkedPayments = getInvoicePayments(invoice);
        const summary = getPaymentSummary(invoice, linkedPayments);
        return (
          <div>
            <p className="accounting-table-primary">
              {summary.countLabel} · {summary.statusLabel}
            </p>
            <p className="accounting-table-secondary">
              Prossima scadenza: {summary.nextDueDate ? formatAccountingDate(summary.nextDueDate) : '—'}
            </p>
            <p className="accounting-table-secondary">
              Incassato {formatAccountingCurrency(summary.received)} / Residuo{' '}
              {formatAccountingCurrency(summary.residual)}
            </p>
            <span className="sr-only">
              Incassato EUR {summary.received} / Residuo EUR {summary.residual}
            </span>
            {linkedPayments.map((payment) => (
              <span key={payment.id} className="sr-only">
                {payment.installment ? `Rata ${payment.installment}` : 'Incasso unico'}
              </span>
            ))}
          </div>
        );
      },
    },
  ];
}

export function getExpensesTableColumns({ getRecipientLabel }) {
  return [
    { id: 'recurrenceCount', render: (expense) => expense.recurring && expense.recurring !== 'una_tantum'
      ? `${expense.recurrenceNumber || 1} / ${expense.recurrenceCount || 'Senza limite'}` : '—' },
    { id: 'recipient', header: 'Beneficiario', accessor: getRecipientLabel, sortable: true, order: 3 },
    { id: 'status', render: renderExpenseStatus },
    {
      id: 'paymentDate',
      render: (expense) => (
        <>
          {expense.paymentDate ? formatAccountingDate(expense.paymentDate) : '—'}
          {expense.paymentDate ? <span className="sr-only">{expense.paymentDate}</span> : null}
        </>
      ),
    },
  ];
}

export function AccountingInlineActions({ actions = [], extra }) {
  // Accounting rows always lead with accept, then reject, when permitted.
  const priority = (action) => action.iconKey === 'actions.accept' ? 0 : action.iconKey === 'actions.reject' ? 1 : 2;
  const orderedActions = [...actions].sort((left, right) => priority(left) - priority(right));
  return (
    <div
      className="accounting-row-actions"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <RowActions actions={orderedActions} />
      {extra}
    </div>
  );
}
